export type TCountry = 'US' | 'CA'
export type TCountryLanguage<T extends TCountry> = {
  readonly US: readonly ['en']
  readonly CA: readonly ['en', 'fr']
}[T][number]

export const SUPPORTED_COUNTRIES = ['US', 'CA'] as const
export const COUNTRY_LANGUAGES = {
  US: ['en'] as const,
  CA: ['en', 'fr'] as const,
} as const
export const LOCALE_COOKIE = 'NEXT_PREFERRED_LOCALE'
export const REGION_DETAILS_LOCAL_STORAGE_KEY = 'REGION_DETAILS'
// @i18n-ignore
export const DEFAULT_LOCALE = 'default-locale'
// @i18n-ignore
export const LOCALE_FORMAT = '{{language}}-{{country}}'
export const DEFAULT_COUNTRY: TCountry = SUPPORTED_COUNTRIES[0]

export interface TLocalisationDataLocalStorage<T extends TCountry = TCountry> {
  regionId: number
  locale: TAbsoluteLocale
  country: T
  language: TCountryLanguage<T>
  currency: string
}

type TLocaleFormat = typeof LOCALE_FORMAT
export type TAbsoluteLocale = {
  [K in TCountry]: TLocaleFormat extends `${infer Prefix}{{country}}${infer Middle}{{language}}${infer Suffix}`
    ? `${Prefix}${K}${Middle}${(typeof COUNTRY_LANGUAGES)[K][number]}${Suffix}`
    : TLocaleFormat extends `${infer Prefix}{{language}}${infer Middle}{{country}}${infer Suffix}`
      ? `${Prefix}${(typeof COUNTRY_LANGUAGES)[K][number]}${Middle}${K}${Suffix}`
      : never
}[TCountry]

export const formatLocale = <T extends TCountry>(country: T, language: TCountryLanguage<T>): TAbsoluteLocale =>
  // eslint-disable-next-line i18next/no-literal-string
  LOCALE_FORMAT.replace('{{country}}', country).replace('{{language}}', language) as TAbsoluteLocale

export const FALLBACK_LOCALE = formatLocale(DEFAULT_COUNTRY, COUNTRY_LANGUAGES[DEFAULT_COUNTRY][0])

export const ABSOLUTE_LOCALES = SUPPORTED_COUNTRIES.flatMap(country =>
  COUNTRY_LANGUAGES[country].map(language => formatLocale(country, language)),
)

export type TAllLocale = typeof DEFAULT_LOCALE | TCountry | TAbsoluteLocale
export const ALL_LOCALES = [DEFAULT_LOCALE, ...SUPPORTED_COUNTRIES, ...ABSOLUTE_LOCALES] as TAllLocale[]
